/**
 * Chat Message Component - Claude Desktop Style
 *
 * Features:
 * - User messages: Dark bubble on right side
 * - Assistant messages: Clean flowing text without avatar/bubble
 * - Reasoning: Bordered collapsible box above content
 * - Actions: Copy, thumbs up/down, retry below content
 */

import * as React from 'react'
import type { Message } from 'ai'
import { Reasoning } from './reasoning'
import { MessageActions } from './message-actions'
import { ArtifactCards, type ArtifactCardData } from './artifact-card'
import { cn } from '@/lib/utils'

interface ReasoningStep {
  type: string
  step?: string
  tools?: string[]
  count?: number
  elapsed?: string
}

interface ChatMessageProps {
  message: Message
  reasoningSteps?: ReasoningStep[]
  reasoningDuration?: string
  tokenUsage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  isStreaming?: boolean
  onRetry?: () => void
  onClickArtifact?: (artifact: ArtifactCardData) => void
  artifacts?: ArtifactCardData[]
}

export function ChatMessage({
  message,
  reasoningSteps = [],
  reasoningDuration,
  tokenUsage,
  isStreaming = false,
  onRetry,
  onClickArtifact,
  artifacts = [],
}: ChatMessageProps) {
  const isUser = message.role === 'user'
  const showActions = !isUser && !isStreaming && message.content

  // User message - bubble on right
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] sm:max-w-[75%]">
          <div className="bg-foreground text-background rounded-2xl rounded-br-md px-4 py-2.5">
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
          </div>
        </div>
      </div>
    )
  }

  // Assistant message - clean flowing text
  return (
    <div className="space-y-3">
      {/* Reasoning box - above content */}
      {reasoningSteps.length > 0 && (
        <Reasoning
          steps={reasoningSteps}
          duration={reasoningDuration}
          tokenUsage={tokenUsage}
          isStreaming={isStreaming}
        />
      )}

      {/* Message content - no bubble, clean text */}
      <div className="prose prose-sm max-w-none text-foreground">
        <MessageContent content={message.content} />
      </div>

      {/* Artifact cards */}
      {artifacts.length > 0 && (
        <ArtifactCards
          artifacts={artifacts}
          onClickArtifact={onClickArtifact}
        />
      )}

      {/* Action buttons - copy, thumbs, retry */}
      {showActions && (
        <MessageActions
          content={message.content}
          onRetry={onRetry}
          showRetry={Boolean(onRetry)}
        />
      )}
    </div>
  )
}

// Enhanced markdown rendering with tables, headings, checkmarks
type BlockType = 'paragraph' | 'list' | 'numbered' | 'checklist' | 'heading' | 'table' | 'separator' | 'blockquote'

interface Block {
  type: BlockType
  lines: string[]
  level?: number // For headings
}

function MessageContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const blocks: Block[] = []
  let currentBlock: Block | null = null
  let tableLines: string[] = []
  let inTable = false

  for (const line of lines) {
    const trimmed = line.trim()

    // Check for table row
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (!inTable) {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = null
        inTable = true
        tableLines = []
      }
      tableLines.push(trimmed)
      continue
    } else if (inTable) {
      // End of table
      blocks.push({ type: 'table', lines: tableLines })
      tableLines = []
      inTable = false
    }

    // Empty line - end current block
    if (!trimmed) {
      if (currentBlock) {
        blocks.push(currentBlock)
        currentBlock = null
      }
      continue
    }

    // Separator ---
    if (/^-{3,}$/.test(trimmed)) {
      if (currentBlock) blocks.push(currentBlock)
      currentBlock = null
      blocks.push({ type: 'separator', lines: [] })
      continue
    }

    // Heading (# ## ###)
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/)
    if (headingMatch && headingMatch[1] && headingMatch[2]) {
      if (currentBlock) blocks.push(currentBlock)
      currentBlock = null
      blocks.push({
        type: 'heading',
        lines: [headingMatch[2].replace(/[🔥🌟📅✅❌👉📚]/g, '').trim()],
        level: headingMatch[1].length
      })
      continue
    }

    // Blockquote
    if (trimmed.startsWith('>')) {
      if (currentBlock?.type !== 'blockquote') {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = { type: 'blockquote', lines: [] }
      }
      currentBlock.lines.push(trimmed.replace(/^>\s*/, ''))
      continue
    }

    // Check line type
    const isBullet = /^[-•*]\s/.test(trimmed)
    const isCheckmark = /^[✅✔️❌⚠️👉]\s*/.test(trimmed)
    const isNumbered = /^\d+[.)]\s/.test(trimmed)

    if (isCheckmark) {
      if (currentBlock?.type !== 'checklist') {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = { type: 'checklist', lines: [] }
      }
      currentBlock.lines.push(trimmed)
    } else if (isBullet) {
      if (currentBlock?.type !== 'list') {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = { type: 'list', lines: [] }
      }
      currentBlock.lines.push(trimmed.replace(/^[-•*]\s/, ''))
    } else if (isNumbered) {
      if (currentBlock?.type !== 'numbered') {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = { type: 'numbered', lines: [] }
      }
      currentBlock.lines.push(trimmed.replace(/^\d+[.)]\s/, ''))
    } else {
      if (currentBlock?.type !== 'paragraph') {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = { type: 'paragraph', lines: [] }
      }
      currentBlock.lines.push(trimmed)
    }
  }

  // Flush remaining
  if (inTable && tableLines.length > 0) {
    blocks.push({ type: 'table', lines: tableLines })
  }
  if (currentBlock) blocks.push(currentBlock)

  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'heading':
            return (
              <div key={i} className={cn(
                'font-semibold text-foreground',
                block.level === 1 && 'text-lg',
                block.level === 2 && 'text-base',
                block.level === 3 && 'text-sm text-muted-foreground'
              )}>
                {formatInlineMarkdown(block.lines[0] ?? '')}
              </div>
            )

          case 'separator':
            return <hr key={i} className="border-border/50 my-3" />

          case 'blockquote':
            return (
              <div key={i} className="border-l-2 border-primary/40 pl-4 text-muted-foreground italic">
                {block.lines.map((line, j) => (
                  <p key={j} className="leading-relaxed">{formatInlineMarkdown(line)}</p>
                ))}
              </div>
            )

          case 'table':
            return <MarkdownTable key={i} lines={block.lines} />

          case 'checklist':
            return (
              <div key={i} className="space-y-2">
                {block.lines.map((item, j) => {
                  const isPositive = /^[✅✔️👉]/.test(item)
                  const isNegative = /^[❌]/.test(item)
                  const isWarning = /^[⚠️]/.test(item)
                  const cleanItem = item.replace(/^[✅✔️❌⚠️👉]\s*/, '')
                  return (
                    <div key={j} className="flex items-start gap-2.5">
                      <span className={cn(
                        'mt-0.5 flex-shrink-0 text-sm',
                        isPositive && 'text-emerald-500',
                        isNegative && 'text-red-500',
                        isWarning && 'text-amber-500'
                      )}>
                        {isPositive ? '✓' : isNegative ? '✗' : '•'}
                      </span>
                      <span className="text-[15px] leading-relaxed">{formatInlineMarkdown(cleanItem)}</span>
                    </div>
                  )
                })}
              </div>
            )

          case 'list':
            return (
              <ul key={i} className="space-y-2">
                {block.lines.map((item, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className="text-primary mt-1.5 flex-shrink-0 text-[8px]">●</span>
                    <span className="text-[15px] leading-relaxed">{formatInlineMarkdown(item)}</span>
                  </li>
                ))}
              </ul>
            )

          case 'numbered':
            return (
              <ol key={i} className="space-y-2">
                {block.lines.map((item, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className="text-muted-foreground font-medium min-w-[1.5rem] flex-shrink-0 text-[15px]">
                      {j + 1}.
                    </span>
                    <span className="text-[15px] leading-relaxed">{formatInlineMarkdown(item)}</span>
                  </li>
                ))}
              </ol>
            )

          default:
            return (
              <p key={i} className="text-[15px] leading-relaxed">
                {formatInlineMarkdown(block.lines.join(' '))}
              </p>
            )
        }
      })}
    </div>
  )
}

// Render markdown table
function MarkdownTable({ lines }: { lines: string[] }) {
  if (lines.length < 2) return null

  const firstLine = lines[0]
  if (!firstLine) return null

  // Parse header and rows
  const parseRow = (line: string) =>
    line.split('|').slice(1, -1).map(cell => cell.trim())

  const headers = parseRow(firstLine)
  // Skip separator line (|---|---|)
  const dataLines = lines.filter((line, i) => i !== 1 && !/^[\s|:-]+$/.test(line.replace(/\|/g, '')))
  const rows = dataLines.slice(1).map(parseRow)

  return (
    <div className="overflow-x-auto my-3 rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2.5 px-4 font-medium text-foreground">
                {formatInlineMarkdown(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={cn(
              'border-b border-border/50 last:border-0',
              i % 2 === 1 && 'bg-muted/20'
            )}>
              {row.map((cell, j) => (
                <td key={j} className="py-2.5 px-4 text-foreground">
                  {formatInlineMarkdown(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Format inline markdown (bold, code)
function formatInlineMarkdown(text: string) {
  // Split by code blocks and bold markers
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g)

  return parts.map((part, i) => {
    // Bold
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    // Inline code
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="px-1.5 py-0.5 rounded-md bg-muted text-[13px] font-mono">
          {part.slice(1, -1)}
        </code>
      )
    }
    return part
  })
}
